export interface AstrologicalRules {
  preferredTithis?: number[];
  preferredNakshatras?: string[];
  primaryTaras?: string[];
  secondaryTaras?: string[];
  avoidedVaras?: string[];
  preferredVaras?: string[];
  preferredHoras?: string[];
  avoidedYogas?: string[];
  preferredYogas?: string[];
  avoidedKaranas?: string[];
  preferredKaranas?: string[];
  source?: string;
  tradition?: string;
  confidence?: 'High' | 'Medium' | 'Derived';
}

export interface ActivityMilestone {
  id: string;
  label: string;
  hiLabel?: string;
  description: string;
  rules: AstrologicalRules;
}

export interface ActivitySubCategory {
  id: string;
  label: string;
  hiLabel?: string;
  description: string;
  milestones?: ActivityMilestone[];
  rules?: AstrologicalRules;
}

export interface ActivityCategory {
  id: string;
  label: string;
  hiLabel?: string;
  description: string;
  subCategories?: ActivitySubCategory[];
  milestones?: ActivityMilestone[];
  rules?: AstrologicalRules;
}

export const MUHURTA_TAXONOMY: ActivityCategory[] = [
  {
    id: 'gemstone_remedial',
    label: 'Gemstone & Remedial',
    hiLabel: 'रत्न एवं वैदिक उपाय',
    description: 'Auspicious timing for wearing Navratnas, Rudraksha, Yantras, and energizing protective rings (Prana Pratishtha).',
    subCategories: [
      {
        id: 'navratna_gemstones',
        label: 'Navratna Gemstones (रत्न धारण)',
        hiLabel: 'नवरत्न धारण',
        description: 'Planetary gemstones wearing and ritual purification (Surya Ruby, Guru Yellow Sapphire, etc.)',
        milestones: [
          {
            id: 'ruby_manik',
            label: 'Ruby / Manik (Sun • सूर्य)',
            hiLabel: 'माणिक्य धारण (सूर्य)',
            description: 'For career leadership, vitality, and authority. Worn on Sunday morning in Shukla Paksha.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Krittika', 'Uttara Phalguni', 'Uttara Ashadha', 'Pushya', 'Hasta'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              preferredVaras: ['Sunday'],
              source: 'Brihat Samhita & Ratna Deepika',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'pearl_moti',
            label: 'Pearl / Moti (Moon • चन्द्र)',
            hiLabel: 'मोती धारण (चन्द्र)',
            description: 'For mental peace, emotional clarity, and mother harmony. Worn on Monday morning in Shukla Paksha.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 15],
              preferredNakshatras: ['Rohini', 'Hasta', 'Sravana', 'Pushya', 'Anuradha'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              preferredVaras: ['Monday'],
              source: 'Ratna Deepika',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'yellow_sapphire_pukhraj',
            label: 'Yellow Sapphire / Pukhraj (Jupiter • गुरु)',
            hiLabel: 'पीला पुखराज (बृहस्पति)',
            description: 'For wisdom, spiritual evolution, prosperity, and marriage. Worn on Thursday morning during Jupiter Hora.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Punarvasu', 'Pushya', 'Vishakha', 'Purva Bhadrapada', 'Rohini', 'Hasta', 'Anuradha'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              preferredVaras: ['Thursday'],
              source: 'Jataka Parijata & Muhurta Chintamani',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'emerald_panna',
            label: 'Emerald / Panna (Mercury • बुध)',
            hiLabel: 'पन्ना धारण (बुध)',
            description: 'For intellect, business commerce, communication, and studies. Worn on Wednesday morning.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Ashlesha', 'Jyeshtha', 'Revati', 'Rohini', 'Hasta', 'Pushya'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Mitra'],
              secondaryTaras: ['Kshema', 'Param Mitra'],
              preferredVaras: ['Wednesday'],
              source: 'Ratna Pariksha',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'coral_moonga',
            label: 'Red Coral / Moonga (Mars • मंगल)',
            hiLabel: 'मूँगा धारण (मंगल)',
            description: 'For courage, property success, stamina, and defense. Worn on Tuesday morning.',
            rules: {
              preferredTithis: [2, 3, 5, 6, 10, 11, 12],
              preferredNakshatras: ['Mrigashirsha', 'Chitra', 'Dhanishta', 'Pushya', 'Rohini', 'Hasta'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              preferredVaras: ['Tuesday'],
              source: 'Ratna Deepika',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'diamond_opal_heera',
            label: 'Diamond / White Sapphire / Opal (Venus • शुक्र)',
            hiLabel: 'हीरा / ओपल / सफेद पुखराज (शुक्र)',
            description: 'For luxury, relationships, beauty, artistic pursuits, and vehicle comforts. Worn on Friday morning.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Bharani', 'Purva Phalguni', 'Purva Ashadha', 'Rohini', 'Pushya', 'Hasta', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              preferredVaras: ['Friday'],
              source: 'Brihat Samhita',
              tradition: 'Classical Ratna Shastra',
              confidence: 'High'
            }
          },
          {
            id: 'blue_sapphire_neelam',
            label: 'Blue Sapphire / Neelam (Saturn • शनि)',
            hiLabel: 'नीलम धारण (शनि)',
            description: 'For career breakthrough, discipline, and endurance (Requires trial). Worn on Saturday evening/twilight.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12],
              preferredNakshatras: ['Pushya', 'Uttara Phalguni', 'Uttara Ashadha', 'Uttara Bhadrapada', 'Chitra', 'Anuradha'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Mitra'],
              secondaryTaras: ['Kshema'],
              preferredVaras: ['Saturday'],
              source: 'Ratna Shastra',
              tradition: 'Classical Saturn Elective',
              confidence: 'High'
            }
          },
          {
            id: 'hessonite_gomed',
            label: 'Hessonite / Gomed (Rahu • राहु)',
            hiLabel: 'गोमेद धारण (राहु)',
            description: 'For sudden opportunities, foreign elevation, and clearing confusion. Worn on Saturday in Swati/Ardra/Shatabhisha.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12],
              preferredNakshatras: ['Ardra', 'Swati', 'Shatabhisha', 'Pushya'],
              primaryTaras: ['Sampat', 'Sadhaka'],
              secondaryTaras: ['Kshema', 'Mitra'],
              preferredVaras: ['Saturday'],
              source: 'Jyotish Ratnamala',
              tradition: 'Classical Remedy',
              confidence: 'High'
            }
          },
          {
            id: 'cats_eye_lehsunia',
            label: 'Cat’s Eye / Lehsunia (Ketu • केतु)',
            hiLabel: 'लहसुनिया धारण (केतु)',
            description: 'For spiritual intuition, occult protection, and Moksha blessings. Worn on Thursday or Tuesday in Ashwini/Magha/Mula.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12],
              preferredNakshatras: ['Aswini', 'Magha', 'Mula', 'Pushya'],
              primaryTaras: ['Sampat', 'Sadhaka'],
              secondaryTaras: ['Kshema', 'Param Mitra'],
              preferredVaras: ['Thursday', 'Tuesday'],
              source: 'Jyotish Ratnamala',
              tradition: 'Classical Remedy',
              confidence: 'High'
            }
          }
        ]
      },
      {
        id: 'rudraksha_yantra',
        label: 'Rudraksha & Yantra Prana Pratishtha',
        hiLabel: 'रुद्राक्ष एवं यंत्र प्रतिष्ठा',
        description: 'Energization and wearing of sacred beads, protective Kavach, and Copper/Silver Yantras.',
        milestones: [
          {
            id: 'rudraksha_wearing',
            label: 'Rudraksha Wearing & Abhishekam',
            hiLabel: 'रुद्राक्ष धारण एवं अभिषेक',
            description: 'First wearing of 1 to 14 Mukhi Rudraksha on Monday or Mahashivratri/Pradosha.',
            rules: {
              preferredTithis: [2, 5, 8, 11, 13, 15],
              preferredNakshatras: ['Rohini', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Sravana', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Mitra'],
              preferredVaras: ['Monday', 'Thursday'],
              source: 'Shiva Purana - Vidyeshvara Samhita',
              tradition: 'Vedic Rudraksha Vidhi',
              confidence: 'High'
            }
          },
          {
            id: 'yantra_pratishtha',
            label: 'Sri Yantra / Kuber Yantra Sthapana',
            hiLabel: 'श्री यंत्र / कुबेर यंत्र स्थापना',
            description: 'Sanctifying and placing prosperity or protection Yantras in home temple or business altar.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              preferredVaras: ['Friday', 'Thursday', 'Wednesday'],
              source: 'Tantra Sara & Mantra Mahodadhi',
              tradition: 'Classical Agamic Sthapana',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'property',
    label: 'Property & Construction',
    hiLabel: 'संपत्ति और निर्माण',
    description: 'Bhoomi Puja, laying foundations, home construction, renovation, and Griha Pravesh.',
    subCategories: [
      {
        id: 'inauguration_entry',
        label: 'Inauguration & Entry',
        hiLabel: 'गृह प्रवेश और उद्घाटन',
        description: 'Entering a house or property for domestic or commercial usage.',
        milestones: [
          {
            id: 'griha_pravesh',
            label: 'Griha Pravesh (House Warming)',
            hiLabel: 'गृह प्रवेश',
            description: 'First entrance into the new house for residential purpose.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              avoidedVaras: ['Tuesday', 'Sunday'],
              source: 'Muhurta Chintamani - Griha Pravesha Prakarana',
              tradition: 'Classical Vastu',
              confidence: 'High'
            }
          },
          {
            id: 'commercial_entry',
            label: 'Commercial Premises Pravesh',
            hiLabel: 'व्यावसायिक परिसर प्रवेश',
            description: 'Starting business operations in a newly built office, shop or showroom.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              avoidedVaras: ['Tuesday'],
              source: 'Vastu Vidya',
              tradition: 'Vedic Commercial',
              confidence: 'High'
            }
          }
        ]
      },
      {
        id: 'construction_stages',
        label: 'Construction Milestones',
        hiLabel: 'निर्माण मील के पत्थर',
        description: 'Specific timings for ground-breaking, foundation, and machinery setup.',
        milestones: [
          {
            id: 'bhoomi_puja',
            label: 'Bhoomi Pujan (Ground-Breaking & Foundation Ceremony)',
            hiLabel: 'भूमि पूजन',
            description: 'Worship of Earth and laying the foundation stone/first brick before construction.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              avoidedVaras: ['Tuesday', 'Sunday'],
              source: 'Vastu Shastra / Mayamata',
              tradition: 'Vedic Architecture',
              confidence: 'High'
            }
          },
          {
            id: 'renovation_start',
            label: 'Renovation & Alteration Start',
            hiLabel: 'नवीनीकरण का प्रारंभ',
            description: 'Starting major repair or structural modification work on existing property.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada'],
              primaryTaras: ['Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Sampat'],
              source: 'Derived Vastu rules',
              tradition: 'Derived / Generalized',
              confidence: 'Medium'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'samskara',
    label: 'Samskara & Life Rites',
    hiLabel: 'संस्कार और जीवन संस्कार',
    description: 'Traditional Vedic life-cycle milestones, ceremonies and family events.',
    subCategories: [
      {
        id: 'marriage',
        label: 'Vivaha & Allied Ceremonies',
        hiLabel: 'विवाह और संबंधित संस्कार',
        description: 'Marriage-related ceremonies, engagement, and family unions.',
        milestones: [
          {
            id: 'vivaha',
            label: 'Vivaha (Marriage Ceremony)',
            hiLabel: 'विवाह',
            description: 'The union of bride and groom. Standard parameters check auspicious lagna and Moon condition.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Kshema', 'Mitra', 'Param Mitra'],
              secondaryTaras: ['Sampat', 'Sadhaka'],
              avoidedVaras: ['Tuesday', 'Saturday'],
              source: 'Muhurta Chintamani - Vivaha Prakarana',
              tradition: 'Pauranika & Vedic',
              confidence: 'High'
            }
          },
          {
            id: 'roka_engagement',
            label: 'Roka & Engagement (Sagai)',
            hiLabel: 'रोका और सगाई / वाग्दान',
            description: 'Pre-wedding promise and formal agreement of upcoming alliance.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Modern adaptation based on Vivaha rules',
              tradition: 'Derived / Generalized',
              confidence: 'Medium'
            }
          }
        ]
      },
      {
        id: 'infancy',
        label: 'Childhood & Infancy Samskaras',
        hiLabel: 'शैशव और बाल संस्कार',
        description: 'Rites performed from birth through childhood education.',
        milestones: [
          {
            id: 'namakarana',
            label: 'Namakarana (Naming Ceremony)',
            hiLabel: 'नामकरण',
            description: 'Timing for assigning the official name to the infant, typically 11th or 12th day after birth.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema', 'Sampat'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Muhurta Chintamani',
              tradition: 'Standard Sanatana',
              confidence: 'High'
            }
          },
          {
            id: 'annaprashana',
            label: 'Annaprashana (First Solid Food)',
            hiLabel: 'अन्नप्राशन',
            description: 'First feeding of solid boiled rice to the child, usually in 6th or 8th month.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Ardra', 'Punarvasu', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              avoidedVaras: ['Tuesday', 'Saturday'],
              source: 'Muhurta Chintamani',
              tradition: 'Classical Vedic',
              confidence: 'High'
            }
          },
          {
            id: 'mundan',
            label: 'Chudakarana / Mundan (Tonsure)',
            hiLabel: 'चूड़ाकर्म / मुंडन',
            description: 'First shaving of the head to detach past life impressions and promote skull health.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 13],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Jyeshta', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Kshema', 'Sadhaka'],
              secondaryTaras: ['Sampat', 'Mitra'],
              avoidedVaras: ['Tuesday', 'Saturday', 'Sunday'],
              source: 'Muhurta Parijata',
              tradition: 'Vedic Sutras',
              confidence: 'High'
            }
          },
          {
            id: 'karnavedha',
            label: 'Karnavedha (Ear Piercing)',
            hiLabel: 'कर्णवेध',
            description: 'Piercing the ears for clean mental hearing and acupuncture advantages.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Mrigashirsha', 'Pushya', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema'],
              secondaryTaras: ['Sampat', 'Mitra'],
              source: 'Muhurta Chintamani',
              tradition: 'Rig Vedic custom',
              confidence: 'High'
            }
          },
          {
            id: 'nishkramana',
            label: 'Nishkramana (First Outing)',
            hiLabel: 'निष्क्रमण',
            description: 'Taking the child out for the first time to witness sunlight, open air, and visit a temple.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Sampat'],
              source: 'Samskara Prakasha',
              tradition: 'Traditional',
              confidence: 'Medium'
            }
          }
        ]
      },
      {
        id: 'academic_rites',
        label: 'Educational Samskaras',
        hiLabel: 'विद्या संस्कार',
        description: 'Academic and learning initiations.',
        milestones: [
          {
            id: 'vidyarambha',
            label: 'Vidyarambha (First Writing)',
            hiLabel: 'विद्यारम्भ',
            description: 'Initiating the child into alphabet writing and basic education.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Ardra', 'Punarvasu', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sadhaka', 'Sampat', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              source: 'Muhurta Chintamani',
              tradition: 'Saraswati Puja tradition',
              confidence: 'High'
            }
          },
          {
            id: 'upanayana',
            label: 'Upanayana (Sacred Thread Ceremony)',
            hiLabel: 'उपनयन',
            description: 'Initiation into formal spiritual studies with the Gayatri Mantra.',
            rules: {
              preferredTithis: [2, 3, 5, 10, 11, 12],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Ardra', 'Punarvasu', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema', 'Param Mitra'],
              secondaryTaras: ['Sampat', 'Mitra'],
              source: 'Yajnavalkya Smriti',
              tradition: 'Classical Brahminical',
              confidence: 'High'
            }
          }
        ]
      },
      {
        id: 'conception',
        label: 'Pre-natal Samskaras (Specialized)',
        hiLabel: 'जन्म पूर्व संस्कार (विशिष्ट)',
        description: 'Ceremonies before birth of the child.',
        milestones: [
          {
            id: 'garbhadhana',
            label: 'Garbhadhana (Conception)',
            hiLabel: 'गर्भाधान',
            description: 'The first of the sixteen Samskaras, purifying the womb for conception.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Uttara Phalguni', 'Uttara Ashadha', 'Uttara Bhadrapada', 'Hasta', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              avoidedVaras: ['Tuesday', 'Sunday'],
              source: 'Brihat Samhita & Muhurta Chintamani',
              tradition: 'Classical Vedic',
              confidence: 'High'
            }
          },
          {
            id: 'pumsavana',
            label: 'Pumsavana (Foetus Protection)',
            hiLabel: 'पुंसवन',
            description: 'Conducted in 2nd or 3rd month to ensure correct development and health of foetus.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 13],
              preferredNakshatras: ['Pushya', 'Rohini', 'Mrigashirsha', 'Hasta', 'Sravana', 'Punardasu', 'Mula'],
              primaryTaras: ['Sadhaka', 'Kshema'],
              secondaryTaras: ['Sampat', 'Mitra'],
              source: 'Muhurta Parijata',
              tradition: 'Samskara Bhaskara',
              confidence: 'High'
            }
          },
          {
            id: 'simantonnayana',
            label: 'Simantonnayana (Baby Shower / Parting of Hair)',
            hiLabel: 'सीमन्तोन्नयन',
            description: 'Conducted in 4th, 6th or 8th month for emotional protection of the expectant mother.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Uttara Bhadrapada'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Samskara Mayukha',
              tradition: 'Classical Smarta',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'business',
    label: 'Business & Commercial',
    hiLabel: 'व्यवसाय और व्यापार',
    description: 'Starting businesses, inaugurations, registrations, agreements, and first sales.',
    subCategories: [
      {
        id: 'business_admin',
        label: 'Company Admin & Setup',
        hiLabel: 'कंपनी स्थापना और व्यवस्था',
        description: 'Legal incorporation, bank setups and partner agreements.',
        milestones: [
          {
            id: 'company_registration',
            label: 'Company Registration / Incorporation',
            hiLabel: 'कंपनी पंजीकरण',
            description: 'Filing legal documents for establishing a private or public limited firm.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Modern Vedic Astrological Consensus',
              tradition: 'Derived / Generalized',
              confidence: 'High'
            }
          },
          {
            id: 'partnership_formation',
            label: 'Partnership Formation',
            hiLabel: 'साझेदारी का गठन',
            description: 'Signing joint deeds for running partnerships or LLPs.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Mitra', 'Param Mitra', 'Kshema'],
              secondaryTaras: ['Sampat', 'Sadhaka'],
              source: 'Astrological Classics on Agreements',
              tradition: 'Vedic Legal',
              confidence: 'High'
            }
          },
          {
            id: 'signing_agreement',
            label: 'Signing Major Business Agreement',
            hiLabel: 'प्रमुख व्यापारिक समझौता हस्ताक्षर',
            description: 'Executing commercial lease deeds, supplier contracts or investment sheets.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Mitra'],
              secondaryTaras: ['Kshema', 'Param Mitra'],
              source: 'Jyotirvidabharanam',
              tradition: 'Classical Commercial',
              confidence: 'High'
            }
          }
        ]
      },
      {
        id: 'business_operations',
        label: 'Inauguration & Transactions',
        hiLabel: 'उद्घाटन और लेनदेन',
        description: 'First business launches, invoicing, and capital generation.',
        milestones: [
          {
            id: 'business_inauguration',
            label: 'Business Inauguration (Ribbon Cutting)',
            hiLabel: 'व्यवसाय का औपचारिक उद्घाटन',
            description: 'The ceremonial starting of the commercial space, open to public and patrons.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              avoidedVaras: ['Tuesday'],
              source: 'Muhurta Chintamani',
              tradition: 'Classical Business',
              confidence: 'High'
            }
          },
          {
            id: 'first_transaction',
            label: 'First Transaction (Invoicing / Sale)',
            hiLabel: 'प्रथम लेनदेन और बिक्री',
            description: 'Receiving the first payment or raising the first billing ledger.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Brihat Samhita',
              tradition: 'Vedic Commercial',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'assets',
    label: 'Asset Purchase',
    hiLabel: 'परिसंपत्ति खरीद',
    description: 'Purchasing properties, land, motor vehicles, gold, jewellery, and factory machinery.',
    subCategories: [
      {
        id: 'real_estate_asset',
        label: 'Property & Land Assets',
        hiLabel: 'भूमि और भवन संपत्ति',
        description: 'Acquiring static estate assets.',
        milestones: [
          {
            id: 'land_purchase',
            label: 'Land / Plot Purchase',
            hiLabel: 'भूमि / भूखंड की खरीद',
            description: 'Timing for signing property purchase agreements or deeds for open lands.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Bhuvanapradipa',
              tradition: 'Vastu Shastra Plot Guidelines',
              confidence: 'High'
            }
          },
          {
            id: 'house_purchase',
            label: 'Ready House / Flat Purchase',
            hiLabel: 'तैयार घर / फ्लैट की खरीद',
            description: 'Paying the advance token or registering completed apartments.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Muhurta Dipika',
              tradition: 'Classical Vastu',
              confidence: 'High'
            }
          }
        ]
      },
      {
        id: 'movable_assets',
        label: 'Movable & Luxury Assets',
        hiLabel: 'चल संपत्ति और वाहन',
        description: 'Buying cars, luxury metals, and heavy machinery.',
        milestones: [
          {
            id: 'vehicle_purchase',
            label: 'Vehicle Purchase (Car / Bike)',
            hiLabel: 'वाहन की खरीद',
            description: 'Taking physical delivery of the vehicle from the dealership.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Punarvasu', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Mitra'],
              secondaryTaras: ['Sadhaka', 'Param Mitra'],
              avoidedVaras: ['Tuesday'],
              source: 'Jyotisha Nibandha',
              tradition: 'Vedic Transit Timing',
              confidence: 'High'
            }
          },
          {
            id: 'gold_purchase',
            label: 'Gold & Fine Jewellery Purchase',
            hiLabel: 'सोना और आभूषण खरीद',
            description: 'Investing in gold bars, gold ornaments or planetary gemstones.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              avoidedVaras: ['Saturday'],
              source: 'Ratna Shastra',
              tradition: 'Vedic Wealth Traditions',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'career',
    label: 'Career & Joining',
    hiLabel: 'करियर और रोजगार',
    description: 'Joining a job, signing contracts, and launching consultations.',
    subCategories: [
      {
        id: 'jobs',
        label: 'Employment Milestones',
        hiLabel: 'रोजगार के मील के पत्थर',
        description: 'Timing critical career starting moments.',
        milestones: [
          {
            id: 'join_job',
            label: 'Joining a New Job / Role',
            hiLabel: 'नई नौकरी / पद पर कार्यभार संभालना',
            description: 'Stepping into the office for your first working day.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Modern Career Astrological Guide',
              tradition: 'Derived / Generalized',
              confidence: 'High'
            }
          },
          {
            id: 'sign_contract',
            label: 'Signing Employment Contract',
            hiLabel: 'रोजगार अनुबंध पर हस्ताक्षर',
            description: 'Accepting the official offer letter and corporate bonds.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Anuradha', 'Sravana', 'Dhanishta', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Sampat'],
              source: 'Jyotisha Classics',
              tradition: 'Vedic Professional',
              confidence: 'Medium'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'education',
    label: 'Education & Admission',
    hiLabel: 'शिक्षा और प्रवेश',
    description: 'Academic admissions, research start, and exam schedules.',
    subCategories: [
      {
        id: 'academics',
        label: 'Academic Milestones',
        hiLabel: 'शैक्षणिक मील के पत्थर',
        description: 'University, board exam, and course study initiations.',
        milestones: [
          {
            id: 'school_admission',
            label: 'First School / University Admission',
            hiLabel: 'प्रथम विद्यालय / विश्वविद्यालय प्रवेश',
            description: 'Formal submission of fees or registration to a premium educational institution.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Punarvasu', 'Pushya', 'Hasta', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema', 'Mitra'],
              secondaryTaras: ['Param Mitra', 'Sampat'],
              source: 'Vidyarambha extensions',
              tradition: 'Vedic Academic',
              confidence: 'High'
            }
          },
          {
            id: 'thesis_start',
            label: 'Starting Research / Thesis Work',
            hiLabel: 'शोध या थीसिस कार्य का प्रारंभ',
            description: 'Writing the first research hypothesis or dissertation draft.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Punarvasu', 'Pushya', 'Hasta', 'Anuradha', 'Sravana', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              source: 'Modern Academic Astrological Consensus',
              tradition: 'Derived / Generalized',
              confidence: 'Medium'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'travel',
    label: 'Travel & Relocation',
    hiLabel: 'यात्रा और पुनर्वास',
    description: 'Long distance travel, foreign visits, and complete house relocation.',
    subCategories: [
      {
        id: 'journeys',
        label: 'Travel Types',
        hiLabel: 'यात्रा के प्रकार',
        description: 'Planning journey timings based on departure.',
        milestones: [
          {
            id: 'foreign_travel',
            label: 'Foreign / Overseas Travel',
            hiLabel: 'विदेश यात्रा',
            description: 'Boarding flight or ships for international destination.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Punarvasu', 'Pushya', 'Hasta', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Kshema', 'Sadhaka', 'Mitra'],
              secondaryTaras: ['Sampat', 'Param Mitra'],
              source: 'Muhurta Chintamani - Yatra Prakarana',
              tradition: 'Vedic Yatra Guidelines',
              confidence: 'High'
            }
          },
          {
            id: 'house_relocation',
            label: 'Permanent House Relocation',
            hiLabel: 'स्थायी गृह स्थानांतरण',
            description: 'Packing and moving furniture into a new rented or purchased property.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Rohini', 'Mrigashirsha', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Vastu Sandarbha',
              tradition: 'Classical Vastu',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'spiritual',
    label: 'Spiritual & Deity Rites',
    hiLabel: 'आध्यात्मिक और धार्मिक कार्य',
    description: 'Yajna, Havan, Mantra initiation, Deity installations, and home Pujas.',
    subCategories: [
      {
        id: 'ceremonies',
        label: 'Sacred Rituals',
        hiLabel: 'पवित्र अनुष्ठान',
        description: 'Vedic fires and mantras.',
        milestones: [
          {
            id: 'havan_yajna',
            label: 'Grah Shanti Havan / Yajna',
            hiLabel: 'गृह शांति हवन / यज्ञ',
            description: 'Laying sacred wood fires for purifying house energy and appeasing planetary deities.',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 'Uttara Ashadha', 'Sravana', 'Dhanishta', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sadhaka', 'Kshema', 'Param Mitra'],
              secondaryTaras: ['Sampat', 'Mitra'],
              source: 'Devata Pratishtha Vidhi',
              tradition: 'Traditional Vedic Rituals',
              confidence: 'High'
            }
          },
          {
            id: 'mantra_initiation',
            label: 'Mantra Diksha (Initiation)',
            hiLabel: 'मंत्र दीक्षा',
            description: 'Receiving a sacred sound or guru mantra for personal spiritual advancement.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13, 15],
              preferredNakshatras: ['Pushya', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati'],
              primaryTaras: ['Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              source: 'Mantra Mahodadhi',
              tradition: 'Tantrika & Vedic Mantras',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'technology',
    label: 'Digital & Technology',
    hiLabel: 'डिजिटल और तकनीक',
    description: 'Domain bookings, server deploys, web and mobile app launch milestones.',
    subCategories: [
      {
        id: 'digital_milestones',
        label: 'Tech Launches',
        hiLabel: 'तकनीकी लॉन्च',
        description: 'Launching digital businesses.',
        milestones: [
          {
            id: 'domain_registration',
            label: 'Domain Name Registration',
            hiLabel: 'डोमेन नाम पंजीकरण',
            description: 'Selecting and booking a business internet address (com/org/in etc.).',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Sampat', 'Sadhaka', 'Kshema'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'],
              secondaryTaras: ['Mitra', 'Param Mitra'],
              source: 'Derived Tech Muhurta Paradigm',
              tradition: 'Modern Elective',
              confidence: 'Medium'
            }
          },
          {
            id: 'website_launch',
            label: 'Website / Software Launch',
            hiLabel: 'वेबसाइट / सॉफ्टवेयर लॉन्च',
            description: 'Making the production URL live to the global internet.',
            rules: {
              preferredTithis: [2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Pushya', 'Rohini', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'],
              secondaryTaras: ['Kshema', 'Mitra'],
              source: 'Derived Tech Muhurta Paradigm',
              tradition: 'Modern Elective',
              confidence: 'Medium'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'healthcare',
    label: 'Medical & Wellness',
    hiLabel: 'चिकित्सा और स्वास्थ्य',
    description: 'Timing for traditional wellness, therapies and cosmetic regimens (Non-Emergency only).',
    subCategories: [
      {
        id: 'wellness',
        label: 'Wellness Initiations',
        hiLabel: 'कल्याण और स्वास्थ्य प्रारंभ',
        description: 'Chronobiology therapies and Ayurvedic cleansing programs.',
        milestones: [
          {
            id: 'ayurveda_detox',
            label: 'Ayurvedic Panchakarma Start',
            hiLabel: 'आयुर्वेदिक पंचकर्म प्रारंभ',
            description: 'Initiating classical full body cleanse and oil therapy (Vamana/Virechana).',
            rules: {
              preferredTithis: [1, 2, 3, 5, 7, 10, 11, 12, 13],
              preferredNakshatras: ['Aswini', 'Rohini', 'Mrigashirsha', 'Pushya', 'Hasta', 'Anuradha', 'Sravana', 'Dhanishta', 'Shatabhisha', 'Revati'],
              primaryTaras: ['Kshema', 'Sadhaka'],
              secondaryTaras: ['Sampat', 'Mitra'],
              source: 'Ashtanga Hridaya Chronotherapy',
              tradition: 'Classical Ayurveda Timing',
              confidence: 'High'
            }
          }
        ]
      }
    ]
  }
];

export const BUSINESS_TYPES = [
  { id: 'manufacturing', label: 'Manufacturing (Engineering / Factories)', hiLabel: 'विनिर्माण (फैक्ट्री और मशीनें)' },
  { id: 'garments', label: 'Garment, Apparel, Textile & Fashion', hiLabel: 'वस्त्र, परिधान और फैशन उद्योग' },
  { id: 'jewellery', label: 'Jewellery, Gems & Precious Metals', hiLabel: 'आभूषण, रत्न और कीमती धातुएं' },
  { id: 'food', label: 'Food, Restaurants, Caterers & Bakeries', hiLabel: 'खाद्य, रेस्तरां, हलवाई और बेकरी' },
  { id: 'transportation', label: 'Transportation, Fleet, Logistics & Cabs', hiLabel: 'परिवहन, रसद, टैक्सी और डिलीवरी' },
  { id: 'real_estate', label: 'Real Estate Development & Brokerage', hiLabel: 'रियल एस्टेट विकास और दलाली' },
  { id: 'tech', label: 'Technology, IT Services, SaaS, Apps & AI', hiLabel: 'तकनीक, आईटी सेवाएं, सॉफ्टवेयर और एआई' },
  { id: 'healthcare', label: 'Healthcare, Clinics, Pharmacies & Diagnostics', hiLabel: 'स्वास्थ्य सेवा, क्लीनिक और फ़ार्मेसी' },
  { id: 'education', label: 'Education, Schools, Coachings & Astrologers', hiLabel: 'शिक्षा, स्कूल, कोचिंग और ज्योतिषी' },
  { id: 'agriculture', label: 'Agriculture, Dairy, Farming & Nurseries', hiLabel: 'कृषि, डेयरी, खेती और पौधशाला' },
  { id: 'services', label: 'Professional Services (Consultancy / Legal)', hiLabel: 'पेशेवर सेवाएं (परामर्श और कानूनी)' },
  { id: 'media', label: 'Media, Films, Publishing & Creative Studios', hiLabel: 'मीडिया, फिल्में, प्रकाशन और रचनात्मक' }
];

export function findMilestoneRules(milestoneId: string) {
  for (const cat of MUHURTA_TAXONOMY) {
    if (cat.milestones) {
      const ms = cat.milestones.find(m => m.id === milestoneId);
      if (ms) return ms;
    }
    if (cat.subCategories) {
      for (const sub of cat.subCategories) {
        if (sub.milestones) {
          const ms = sub.milestones.find(m => m.id === milestoneId);
          if (ms) return ms;
        }
      }
    }
  }
  return null;
}
